import { FieldRequirement } from "./FieldRequirement";
import { useEffect, useState } from "react";
import type { Organization } from "../domain";
import { request } from "../api/client";
import { useLocale } from "../i18n";
import { AppSelect } from "./inputs/AppSelect";
import { BuildingOffice2Icon } from "@heroicons/react/24/outline";

export function OrganizationSwitcher({ current, onChange, busy }: {
  current: Organization; onChange: (id: string) => Promise<void>; busy: boolean;
}) {
  const { w } = useLocale();
  const [items, setItems] = useState<Organization[]>([current]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(false);
    request<{ items: Organization[]; nextCursor: string | null }>("/organizations", "GET", undefined, controller.signal)
      .then(page => {
        if (controller.signal.aborted) return;
        setItems([current, ...page.items.filter(org => org.id !== current.id)]); setCursor(page.nextCursor);
      }).catch(() => { if (!controller.signal.aborted) setError(true); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [current.id, current.name, retry]);
  async function more() {
    setLoading(true); setError(false);
    try {
      const page = await request<{ items: Organization[]; nextCursor: string | null }>("/organizations?cursor=" + encodeURIComponent(cursor || ""));
      if (page.nextCursor && page.nextCursor === cursor) throw new Error("Invalid organization page");
      setItems(previous => [...previous, ...page.items.filter(org => !previous.some(old => old.id === org.id))]);
      setCursor(page.nextCursor);
    } catch { setError(true); } finally { setLoading(false); }
  }
  return <section className="organization-switcher" aria-busy={busy || loading}>
    <label htmlFor="current-organization"><span className="field-caption"><BuildingOffice2Icon aria-hidden="true" />{w("المنشأة الحالية", "Current organization")}<FieldRequirement required={true} /></span></label>
    <AppSelect id="current-organization" title={current.name} value={current.id} required inlineMenu disabled={busy || loading}
      onChange={id => { setError(false); void onChange(id).catch(() => setError(true)); }}>
      {items.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
    </AppSelect>
    {busy && <p role="status">{w("جارٍ تبديل المنشأة…", "Switching organization…")}</p>}
    {cursor && <button className="text-link" type="button" disabled={busy || loading} onClick={() => void more()}>{w("عرض المزيد من منشآتي", "Load more organizations")}</button>}
    {error && <div role="alert"><p>{w("تعذر تحديث المنشأة. حاول مجدداً.", "Could not update organization. Please retry.")}</p><button className="text-link" type="button" onClick={() => setRetry(value => value + 1)}>{w("تحديث القائمة", "Refresh list")}</button></div>}
  </section>;
}
